import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanGroupedProduct, FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { ProjectEventType } from "@domain/project/project-event";
import { IsArrayOfInstancesOf } from "@utils/class-validator/class-validator-constraints";
import { Expose, Transform, Type } from "class-transformer";
import { IsNumber, ValidateNested } from "class-validator";

export abstract class FloorplanPlaceholder extends FloorplanItem {
  @Expose({ name: "size" })
  @IsNumber()
  protected _size: number;

  @ValidateNested({ each: true })
  @IsArrayOfInstancesOf(PlaceholderProduct)
  @Type(() => PlaceholderProduct)
  @Expose({ name: "products" })
  @Transform((params) => params.value || [], { toClassOnly: true })
  protected _products: PlaceholderProduct[];

  protected constructor(floorplan: Floorplan, id: string, x: number, y: number, size: number, notes?: string) {
    super(floorplan, id, x, y, notes);
    this._size = size;
    this._products = [];
  }

  override isGroupedProduct(): this is FloorplanGroupedProduct {
    return true;
  }

  override supportsPlaceholderPositionIds(): boolean {
    return true;
  }

  override refreshPlaceholderPositionIds(positionNumber?: number): boolean {
    this._floorplan.project.refreshPlaceholderPositionIds(this, positionNumber);
    return true;
  }

  get size(): number {
    return this._size;
  }

  get products(): PlaceholderProduct[] {
    return this._products;
  }

  set products(products: PlaceholderProduct[]) {
    this._products = products;
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_UPDATED, this);
    this._floorplan.project.publishUpdate(ProjectEventType.PLACEHOLDER_PRODUCTS_UPDATED, this);
  }

  updateSize(value: number, x: number, y: number) {
    this._size = value;
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_SIZE_CHANGED, this);
    this.updatePosition(x, y);
  }

  protected capturePlaceholderProducts(): PlaceholderProduct[] {
    return this.products.map((product) => product.copy());
  }

  protected copyPlaceholderProductsTo(duplicate: FloorplanPlaceholder, products: PlaceholderProduct[]) {
    duplicate._products = products.map((product) => product.copy());
  }

  protected override afterDuplicateAdded(): ((duplicate: FloorplanItem) => void) | undefined {
    return this.afterGroupedDuplicateAdded();
  }
}
